import React, { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import './Inventory.css';
import './Market.css';
import InventoryBagGrid from './inventory/InventoryBagGrid';
import InventoryItemPopup from './inventory/InventoryItemPopup';
import useInventoryItemPopupActions from './inventory/useInventoryItemPopupActions';
import useInventoryGridDragDrop from './inventory/useInventoryGridDragDrop';
import {
  buildInventoryGridFromBootstrap,
  canPlaceItemInGrid,
  INVENTORY_TYPE_CONFIG,
} from './inventory/inventoryGridBootstrap';
import {
  GRID_COLUMNS,
  GRID_SIZE,
  buildPublicImageUrl,
  computePageCoverage,
  getFootprintIndexes,
  getItemGridSize,
} from './inventory/inventoryBagGridUtils';
import { buildDetailedStatRows } from '../utils/itemStatUtils';
import { authButtonSmallStyle } from '../constants/authStyles';
import { fetchInventoryBootstrap } from '../utils/inventoryBootstrapClient';

const SHOP_GRID_ROWS = 2;
const SHOP_GRID_SIZE = GRID_COLUMNS * SHOP_GRID_ROWS;

const npcShopMenuBgCssVars = (() => {
  const p = process.env.PUBLIC_URL ?? '';
  return {
    '--inv-slot-bg': `url(${p}/image/menu/kutuuzun.png)`,
    '--inv-adam-bg': `url(${p}/image/menu/adam.png)`,
    '--inv-kutukare-bg': `url(${p}/image/menu/kutukare.png)`,
  };
})();

const emptyGrid = (size) => Array(size).fill(null);

const setNativeDragGhost = (event, imageUrl) => {
  if (!event?.dataTransfer || !imageUrl) return;
  const ghostImage = document.createElement('img');
  ghostImage.src = imageUrl;
  ghostImage.width = 48;
  ghostImage.height = 48;
  ghostImage.style.position = 'absolute';
  ghostImage.style.top = '-1000px';
  ghostImage.style.left = '-1000px';
  ghostImage.style.opacity = '0.9';
  document.body.appendChild(ghostImage);
  event.dataTransfer.setDragImage(ghostImage, 24, 24);
  setTimeout(() => {
    if (ghostImage.parentNode) ghostImage.parentNode.removeChild(ghostImage);
  }, 0);
};

const getCategoryLabel = (itemType, ti) => {
  const config = INVENTORY_TYPE_CONFIG.find((entry) => entry.slotType === itemType);
  return config ? ti(config.labelKey) : itemType;
};

const SHOP_ITEM_TYPE_TO_CATALOG = {
  weapon: 'weapons',
  armor: 'armors',
  helmet: 'helmets',
  boot: 'boots',
  earring: 'earrings',
  necklace: 'necklaces',
  shield: 'shields',
  pot: 'pots',
  other: 'others',
};

const findCatalogEntry = (raw, catalog) => {
  const catalogKey = SHOP_ITEM_TYPE_TO_CATALOG[raw.itemType];
  if (!catalogKey || !Array.isArray(catalog?.[catalogKey])) return null;
  return catalog[catalogKey].find((entry) => (
    entry.id === raw.itemId
    || (entry.baseItemId === raw.itemId && (entry.enhancement ?? 0) === 0)
  )) || null;
};

const mapNpcShopItem = (raw, ti, catalog = {}) => {
  const slotType = raw.itemType;
  const catalogEntry = findCatalogEntry(raw, catalog);
  const gridWidth = catalogEntry?.gridWidth || raw.gridWidth || 1;
  const gridHeight = catalogEntry?.gridHeight || raw.gridHeight || 1;
  return {
    uniqueKey: `npc-shop-${raw.itemType}-${raw.itemId}`,
    itemType: raw.itemType,
    itemId: raw.itemId,
    price: raw.price,
    count: raw.count || 1,
    slotType,
    categoryLabel: getCategoryLabel(slotType, ti),
    baseItem: {
      name: raw.itemName,
      imageUrl: raw.imageUrl,
      level: raw.level,
      enhancement: raw.enhancement,
      attackMin: raw.attackMin,
      attackMax: raw.attackMax,
      magicAttackMin: raw.magicAttackMin,
      magicAttackMax: raw.magicAttackMax,
      attackSpeed: raw.attackSpeed,
      defense: raw.defense,
      moveSpeed: raw.moveSpeed,
      value: raw.value,
      characterId: raw.characterId,
      gridWidth,
      gridHeight,
    },
  };
};

const canFitShopItemInInventory = (gridSlots, shopItem, unlockedCount) => {
  for (let index = 0; index < unlockedCount; index += 1) {
    const result = canPlaceItemInGrid({
      gridSlots,
      targetIndex: index,
      movingItem: shopItem,
      unlockedCount,
    });
    if (result.ok) return true;
  }
  return false;
};

const withShopPurchaseState = (item, userGold, gridSlots, unlockedCount) => {
  const canAfford = userGold >= item.price;
  const canFitInInventory = canFitShopItemInInventory(gridSlots, item, unlockedCount);
  return {
    ...item,
    canAfford,
    canFitInInventory,
    canBuy: canAfford && canFitInInventory,
  };
};

const canPlaceInShopGrid = (gridSlots, targetIndex, movingItem, gridSize) => {
  if (!movingItem || targetIndex < 0 || targetIndex >= gridSize) return false;
  const { width, height } = getItemGridSize(movingItem);
  const targetFootprint = getFootprintIndexes(targetIndex, width, height, gridSize);
  if (!targetFootprint) return false;

  const { coveredIndexes } = computePageCoverage(gridSlots, 0, gridSize);
  return targetFootprint.every((idx) => !gridSlots[idx] && !coveredIndexes.has(idx));
};

const buildNpcShopGrid = (shopItems, userGold, ti, inventoryGridSlots, unlockedCount, catalog) => {
  const grid = emptyGrid(SHOP_GRID_SIZE);
  const mappedItems = (shopItems || []).map((raw) => (
    withShopPurchaseState(mapNpcShopItem(raw, ti, catalog), userGold, inventoryGridSlots, unlockedCount)
  ));

  mappedItems.forEach((item) => {
    for (let index = 0; index < SHOP_GRID_SIZE; index += 1) {
      if (canPlaceInShopGrid(grid, index, item, SHOP_GRID_SIZE)) {
        grid[index] = item;
        break;
      }
    }
  });

  return grid;
};

const getGridCellPosition = (index, pageStart = 0) => {
  const localIndex = index - pageStart;
  return {
    colStart: (localIndex % GRID_COLUMNS) + 1,
    rowStart: Math.floor(localIndex / GRID_COLUMNS) + 1,
  };
};

const buildGridSlotStyle = (index, pageStart, span) => {
  const { colStart, rowStart } = getGridCellPosition(index, pageStart);
  return {
    gridColumnStart: colStart,
    gridRowStart: rowStart,
    ...(span ? {
      gridColumnEnd: `span ${span.width}`,
      gridRowEnd: `span ${span.height}`,
      aspectRatio: `${span.width} / ${span.height}`,
      zIndex: 2,
    } : {}),
  };
};

const NpcShopWorkbench = ({
  npcId,
  username,
  userGold,
  refreshCharacter,
  setError,
  clearError,
  onBuySuccess,
}) => {
  const { t } = useTranslation('village');
  const { t: tc } = useTranslation('common');
  const { t: ti } = useTranslation('inventory');
  const { t: tm } = useTranslation('market');

  const workbenchRef = useRef(null);
  const inventoryGridAreaRef = useRef(null);
  const touchStartPointRef = useRef({ x: 0, y: 0 });
  const touchHasMovedRef = useRef(false);
  const touchDragSourceRef = useRef(null);
  const touchScrollSnapshotRef = useRef(null);
  const shopSkipNextClickRef = useRef(false);

  const [shopSlots, setShopSlots] = useState(emptyGrid(SHOP_GRID_SIZE));
  const [shopLoading, setShopLoading] = useState(true);
  const [shopEmpty, setShopEmpty] = useState(false);
  const [inventoryGridSlots, setInventoryGridSlots] = useState(emptyGrid(GRID_SIZE));
  const [unlockedSlotsCount, setUnlockedSlotsCount] = useState(60);
  const [currentInventoryPage, setCurrentInventoryPage] = useState(1);
  const [inventoryLoading, setInventoryLoading] = useState(true);
  const [draggedShopIndex, setDraggedShopIndex] = useState(null);
  const [dragOverInventoryIndex, setDragOverInventoryIndex] = useState(null);
  const [activePopup, setActivePopup] = useState(null);
  const [buying, setBuying] = useState(false);
  const [isTouchDragging, setIsTouchDragging] = useState(false);
  const [touchDragGhost, setTouchDragGhost] = useState(null);
  const [rawShopItems, setRawShopItems] = useState([]);
  const [itemCatalog, setItemCatalog] = useState({});

  const loadShopItems = useCallback(async () => {
    if (!npcId) return;
    setShopLoading(true);
    clearError?.();
    try {
      const response = await axios.get(`/api/npc-shop?npcId=${npcId}`);
      if (response.data?.success && Array.isArray(response.data.items)) {
        setRawShopItems(response.data.items);
        setShopEmpty(response.data.items.length === 0);
      } else {
        setRawShopItems([]);
        setShopEmpty(true);
      }
    } catch (err) {
      console.error('Error fetching NPC shop:', err);
      setRawShopItems([]);
      setShopEmpty(true);
      setError?.(t('shopBuyError'));
    } finally {
      setShopLoading(false);
    }
  }, [npcId, clearError, setError, t]);

  useEffect(() => {
    loadShopItems();
  }, [loadShopItems]);

  useEffect(() => {
    setShopSlots(buildNpcShopGrid(
      rawShopItems,
      userGold,
      ti,
      inventoryGridSlots,
      unlockedSlotsCount,
      itemCatalog
    ));
  }, [rawShopItems, userGold, ti, inventoryGridSlots, unlockedSlotsCount, itemCatalog]);

  const inventoryLoadedRef = useRef(false);
  const loadInventory = useCallback(async () => {
    if (!username) return null;
    // "Yükleniyor" sadece ilk açılışta; sonraki yenilemelerde envanter ekranda kalır.
    if (!inventoryLoadedRef.current) setInventoryLoading(true);
    try {
      const response = await fetchInventoryBootstrap(username);
      if (!response.data?.success) throw new Error('bootstrap failed');
      const { gridSlots, unlockedSlotsCount: nextUnlocked } = await buildInventoryGridFromBootstrap({
        username,
        bootstrapData: response.data,
        equipment: response.data.equipment || null,
        t: ti,
      });
      setInventoryGridSlots(gridSlots);
      setUnlockedSlotsCount(nextUnlocked);
      setItemCatalog(response.data.catalog || {});
      return gridSlots;
    } catch (err) {
      console.error('Error loading shop inventory:', err);
      setError?.(t('enhanceItemsFetchFailed'));
      return null;
    } finally {
      inventoryLoadedRef.current = true;
      setInventoryLoading(false);
    }
  }, [username, ti, setError, t]);

  useEffect(() => {
    loadInventory();
  }, [loadInventory]);

  const {
    activeItemPopup,
    setActiveItemPopup,
    handleGridItemClick: handleInventoryPopupClick,
    buildPopupProps,
  } = useInventoryItemPopupActions({
    username,
    onAfterAction: async (opts) => {
      await loadInventory();
      if (opts?.refreshGold) {
        await refreshCharacter().catch(() => {});
      }
    },
    setError,
    clearError,
    refreshCharacter,
  });

  const canPlaceShopItemAtSlot = useCallback((item, slotIndex) => {
    if (!item || slotIndex == null || slotIndex < 0) return false;
    return canPlaceItemInGrid({
      gridSlots: inventoryGridSlots,
      targetIndex: slotIndex,
      movingItem: item,
      unlockedCount: unlockedSlotsCount,
    }).ok;
  }, [inventoryGridSlots, unlockedSlotsCount]);

  const buyShopItem = useCallback(async (item, slotIndex = null) => {
    if (!item || buying) return false;

    const hasPreferredSlot = Number.isInteger(slotIndex) && slotIndex >= 0;
    if (!item.canAfford) {
      setError?.(t('shopInsufficientGold', { required: item.price, current: userGold }));
      return false;
    }
    if (hasPreferredSlot) {
      if (!canPlaceShopItemAtSlot(item, slotIndex)) {
        setError?.(t('shopInventoryFull'));
        return false;
      }
    } else if (!item.canFitInInventory) {
      setError?.(t('shopInventoryFull'));
      return false;
    }

    setBuying(true);
    clearError?.();
    try {
      const payload = {
        username,
        npcId,
        itemType: item.itemType,
        itemId: item.itemId,
      };
      if (hasPreferredSlot) {
        payload.slotIndex = slotIndex;
      }
      const response = await axios.post('/api/npc-shop/buy', payload);
      if (response.data?.success) {
        await refreshCharacter().catch(() => {});
        await loadInventory();
        onBuySuccess?.(item.baseItem?.name || tc('unknownItem'));
        return true;
      }
      setError?.(response.data?.message || t('shopBuyFailed'));
      return false;
    } catch (err) {
      setError?.(err.response?.data?.message || t('shopBuyError'));
      return false;
    } finally {
      setBuying(false);
    }
  }, [
    buying,
    userGold,
    setError,
    clearError,
    username,
    npcId,
    refreshCharacter,
    loadInventory,
    onBuySuccess,
    canPlaceShopItemAtSlot,
    t,
    tc,
  ]);

  const {
    dragOverInventoryIndex: inventoryDragOverIndex,
    touchDropTargetIndex,
    isTouchDragging: isInventoryTouchDragging,
    touchDragGhost: inventoryTouchGhost,
    skipNextClickRef,
    isGridDropBlocked,
    handleGridDragStart,
    handleGridDragEnd,
    handleInventorySlotDragOver,
    handleInventorySlotDragLeave,
    handleGridDrop,
    handleInventoryTabDragOver,
    handleGridTouchStart,
    handleGridTouchMove,
    handleGridTouchEnd,
    handleGridTouchCancel,
  } = useInventoryGridDragDrop({
    username,
    inventoryGridSlots,
    setInventoryGridSlots,
    unlockedSlotsCount,
    currentInventoryPage,
    setCurrentInventoryPage,
    inventoryGridAreaRef,
    setError,
    errorDropBlockedMessage: ti('errorDropBlocked'),
    layoutSaveFailedMessage: ti('errorLayoutSaveFailed'),
    disabled: buying || draggedShopIndex !== null,
    onInventoryChanged: loadInventory,
    potStackFullMessage: ti('errorPotStackFull', { max: 200 }),
  });

  const resolveShopItemFromEvent = (event) => {
    if (draggedShopIndex !== null && shopSlots[draggedShopIndex]) {
      return shopSlots[draggedShopIndex];
    }
    if (!event?.dataTransfer) return null;
    const source = event.dataTransfer.getData('text/npc-shop-source');
    if (source !== 'shop') return null;
    const itemType = event.dataTransfer.getData('text/shop-item-type');
    const itemId = Number(event.dataTransfer.getData('text/shop-item-id'));
    if (!itemType || !Number.isInteger(itemId)) return null;
    return shopSlots.find((slotItem) => slotItem?.itemType === itemType && slotItem?.itemId === itemId) || null;
  };

  const clearShopDragState = () => {
    setDraggedShopIndex(null);
    setDragOverInventoryIndex(null);
  };

  const lockTouchDragScroll = () => {
    if (touchScrollSnapshotRef.current) return;
    touchScrollSnapshotRef.current = {
      bodyOverflow: document.body.style.overflow,
      bodyTouchAction: document.body.style.touchAction,
    };
    document.body.style.overflow = 'hidden';
    document.body.style.touchAction = 'none';
  };

  const unlockTouchDragScroll = () => {
    const snapshot = touchScrollSnapshotRef.current;
    if (!snapshot) return;
    document.body.style.overflow = snapshot.bodyOverflow;
    document.body.style.touchAction = snapshot.bodyTouchAction;
    touchScrollSnapshotRef.current = null;
  };

  const getTouchTargetFromPoint = (x, y) => {
    const elementAtPoint = document.elementFromPoint(x, y);
    const shopSlot = elementAtPoint?.closest?.('[data-npc-shop-slot-index]');
    if (shopSlot) {
      const index = Number(shopSlot.dataset.npcShopSlotIndex);
      return Number.isInteger(index) ? { area: 'shop', index } : null;
    }

    const inventorySlot = elementAtPoint?.closest?.('[data-slot-index]');
    if (inventorySlot?.closest?.('.npc-shop-inventory-grid-wrapper')) {
      const index = Number(inventorySlot.dataset.slotIndex);
      return Number.isInteger(index) ? { area: 'inventory', index } : null;
    }

    const pageTab = elementAtPoint?.closest?.('[data-inventory-page]');
    if (pageTab?.closest?.('.npc-shop-inventory-grid-wrapper, .npc-shop-page')) {
      const page = Number(pageTab.dataset.inventoryPage);
      return Number.isInteger(page) ? { area: 'inventoryPage', page } : null;
    }

    return null;
  };

  const handleShopDragStart = (event, index, item) => {
    if (!item || buying) {
      event.preventDefault();
      return;
    }
    setActivePopup(null);
    setActiveItemPopup(null);
    event.dataTransfer.effectAllowed = 'copy';
    event.dataTransfer.setData('text/npc-shop-source', 'shop');
    event.dataTransfer.setData('text/shop-index', String(index));
    event.dataTransfer.setData('text/shop-item-type', item.itemType);
    event.dataTransfer.setData('text/shop-item-id', String(item.itemId));
    setNativeDragGhost(event, buildPublicImageUrl(item.baseItem?.imageUrl));
    setDraggedShopIndex(index);
  };

  const handleShopDragEnd = () => {
    clearShopDragState();
  };

  const handleInventoryDragOver = (event, index) => {
    if (draggedShopIndex !== null || event.dataTransfer?.types?.includes('text/npc-shop-source')) {
      const item = draggedShopIndex !== null ? shopSlots[draggedShopIndex] : resolveShopItemFromEvent(event);
      if (!item) return;
      const canDrop = item.canAfford && canPlaceShopItemAtSlot(item, index);
      event.preventDefault();
      event.dataTransfer.dropEffect = canDrop ? 'copy' : 'none';
      setDragOverInventoryIndex(index);
      return;
    }
    handleInventorySlotDragOver(event, index);
  };

  const handleInventoryDragLeave = (index) => {
    if (draggedShopIndex !== null) {
      setDragOverInventoryIndex((prev) => (prev === index ? null : prev));
      return;
    }
    handleInventorySlotDragLeave();
  };

  const handleInventoryDrop = async (event, index) => {
    const shopItem = resolveShopItemFromEvent(event);
    if (shopItem || draggedShopIndex !== null) {
      event.preventDefault();
      const item = shopItem || shopSlots[draggedShopIndex];
      clearShopDragState();
      if (item) {
        await buyShopItem(item, index);
      }
      return;
    }
    await handleGridDrop(event, index);
  };

  const isInventoryDropBlocked = (index) => {
    if (draggedShopIndex !== null) {
      const item = shopSlots[draggedShopIndex];
      return !(item?.canAfford && canPlaceShopItemAtSlot(item, index));
    }
    return isGridDropBlocked(index);
  };

  const handleShopItemClick = (event, item, index) => {
    if (shopSkipNextClickRef.current) {
      shopSkipNextClickRef.current = false;
      return;
    }
    if (!item) {
      setActivePopup(null);
      return;
    }
    const isSameSlotOpen = activePopup && activePopup.index === index;
    if (isSameSlotOpen) {
      setActivePopup(null);
      return;
    }
    setActiveItemPopup(null);
    setActivePopup({ index, item });
  };

  const handleInventoryClick = (event, slotItem, index) => {
    if (skipNextClickRef.current || shopSkipNextClickRef.current) {
      skipNextClickRef.current = false;
      shopSkipNextClickRef.current = false;
      return;
    }
    setActivePopup(null);
    handleInventoryPopupClick(slotItem, index, inventoryGridAreaRef);
  };

  const handleShopTouchStart = (event, index, item) => {
    if (!item || buying) return;
    const touch = event.touches?.[0];
    if (!touch) return;
    lockTouchDragScroll();
    touchStartPointRef.current = { x: touch.clientX, y: touch.clientY };
    touchHasMovedRef.current = false;
    touchDragSourceRef.current = { source: 'shop', index, item };
    setActivePopup(null);
    setActiveItemPopup(null);
    setIsTouchDragging(true);
    setTouchDragGhost({
      imageUrl: buildPublicImageUrl(item.baseItem?.imageUrl),
      x: touch.clientX,
      y: touch.clientY,
    });
    setDraggedShopIndex(index);
  };

  const handleTouchMove = (event) => {
    const source = touchDragSourceRef.current;
    if (!source) return;
    const touch = event.touches?.[0];
    if (!touch) return;

    setTouchDragGhost((prev) => (prev ? { ...prev, x: touch.clientX, y: touch.clientY } : prev));

    const dx = Math.abs(touch.clientX - touchStartPointRef.current.x);
    const dy = Math.abs(touch.clientY - touchStartPointRef.current.y);
    if (dx > 6 || dy > 6) {
      touchHasMovedRef.current = true;
    }

    const target = getTouchTargetFromPoint(touch.clientX, touch.clientY);
    if (target?.area === 'inventoryPage' && source.source === 'shop') {
      if (target.page !== currentInventoryPage) {
        setCurrentInventoryPage(target.page);
      }
      setDragOverInventoryIndex(null);
      return;
    }

    setDragOverInventoryIndex(
      target?.area === 'inventory' && source.source === 'shop' ? target.index : null
    );
  };

  const clearTouchDragState = () => {
    unlockTouchDragScroll();
    touchHasMovedRef.current = false;
    touchDragSourceRef.current = null;
    setIsTouchDragging(false);
    setTouchDragGhost(null);
    clearShopDragState();
  };

  const handleTouchEnd = async (event) => {
    const source = touchDragSourceRef.current;
    if (!source) return;

    const changedTouch = event.changedTouches?.[0];
    if (touchHasMovedRef.current && changedTouch && source.source === 'shop') {
      const target = getTouchTargetFromPoint(changedTouch.clientX, changedTouch.clientY);
      if (target?.area === 'inventory') {
        await buyShopItem(source.item, target.index);
        shopSkipNextClickRef.current = true;
      }
    }

    clearTouchDragState();
  };

  const renderShopItem = (item) => (
    <>
      {item?.baseItem?.imageUrl ? (
        <span className="slot-item-image">
          <img src={buildPublicImageUrl(item.baseItem.imageUrl)} alt={item.baseItem.name} draggable={false} />
        </span>
      ) : null}
      {item?.count > 1 ? <span className="inventory-slot-count">x{item.count}</span> : null}
    </>
  );

  const renderShopPopup = () => {
    if (!activePopup?.item) return null;
    const item = activePopup.item;
    const rows = buildDetailedStatRows({
      ...item.baseItem,
      categoryLabel: item.categoryLabel,
      count: item.count,
    });

    return (
      <div className="inventory-item-popup-backdrop" onClick={() => setActivePopup(null)}>
        <div
          className="inventory-item-popup inventory-item-popup--modal market-item-popup"
          onClick={(event) => event.stopPropagation()}
        >
          <button
            type="button"
            className="inventory-item-popup-close"
            onClick={() => setActivePopup(null)}
            aria-label={tc('close')}
          >
            X
          </button>
          <div className="market-popup-content">
            <div className="market-popup-details">
              <h4>{item.baseItem?.name || tc('unknownItem')}</h4>
              <p>{tc('category')}: {item.categoryLabel}</p>
              {item.baseItem?.level > 0 ? (
                <p>{tc('level')}: {item.baseItem.level}</p>
              ) : null}
              {item.price != null ? (
                <p>{tc('price')}: {tm('priceYang', { amount: item.price })}</p>
              ) : null}
              {rows.map((row, rowIndex) => (
                <p key={`${row.label}-${rowIndex}`}>{row.label}: {row.value}</p>
              ))}
            </div>
            {item.baseItem?.imageUrl ? (
              <div className="market-popup-image">
                <img
                  src={buildPublicImageUrl(item.baseItem.imageUrl)}
                  alt={item.baseItem?.name || tc('unknownItem')}
                  draggable={false}
                />
              </div>
            ) : null}
          </div>
          <button
            type="button"
            className="auth-button auth-button--compact"
            style={authButtonSmallStyle}
            disabled={buying || !item.canBuy}
            onClick={async () => {
              const ok = await buyShopItem(item);
              if (ok) setActivePopup(null);
            }}
          >
            {buying ? tc('buying') : tc('buy')}
          </button>
        </div>
      </div>
    );
  };

  const renderShopGrid = () => {
    const { coveredIndexes, spanByIndex } = computePageCoverage(shopSlots, 0, SHOP_GRID_SIZE);
    return (
      <div className="inventory-grid-wrapper market-grid-wrapper npc-shop-grid-wrapper">
        <div className="inventory-grid-area">
          <div className="market-grid inventory-grid-10x10">
            {shopSlots.map((item, index) => {
              if (coveredIndexes.has(index)) return null;
              const span = spanByIndex[index];
              return (
                <div
                  key={`npc-shop-slot-${index}`}
                  className={`inventory-slot-box market-slot ${item ? 'filled' : 'empty'} ${span ? 'multi-size' : ''} ${item ? (item.canBuy ? 'market-slot--affordable' : 'market-slot--unaffordable') : ''}`}
                  data-npc-shop-slot-index={index}
                  style={buildGridSlotStyle(index, 0, span)}
                  draggable={!!item && !buying}
                  onDragStart={(event) => handleShopDragStart(event, index, item)}
                  onDragEnd={handleShopDragEnd}
                  onTouchStart={(event) => item && handleShopTouchStart(event, index, item)}
                  onClick={(event) => handleShopItemClick(event, item, index)}
                >
                  {renderShopItem(item)}
                </div>
              );
            })}
          </div>
        </div>
      </div>
    );
  };

  const itemPopupProps = buildPopupProps();
  const combinedTouchDragging = isTouchDragging || isInventoryTouchDragging;
  const combinedTouchGhost = touchDragGhost || inventoryTouchGhost;
  const combinedDragOverIndex = draggedShopIndex !== null
    ? dragOverInventoryIndex
    : (touchDropTargetIndex ?? inventoryDragOverIndex);

  return (
    <>
      {renderShopPopup()}
      {itemPopupProps ? <InventoryItemPopup {...itemPopupProps} /> : null}
      <div
        className={`npc-shop-section${combinedTouchDragging ? ' npc-shop-section--touch-dragging' : ''}`}
        style={npcShopMenuBgCssVars}
        ref={workbenchRef}
        onTouchMoveCapture={handleTouchMove}
        onTouchEndCapture={handleTouchEnd}
        onTouchCancelCapture={clearTouchDragState}
      >
        <div className="npc-shop-workbench">
          <div className="npc-shop-top">
            <h3 className="auth-selection-section-title">{t('shopTitle')}</h3>
            {shopLoading ? (
              <p className="no-items">{t('shopLoading')}</p>
            ) : shopEmpty ? (
              <p className="no-items">{t('shopEmpty')}</p>
            ) : (
              renderShopGrid()
            )}
          </div>

          <div className="inventory-list npc-shop-inventory-section">
            <h3 className="auth-selection-section-title">{t('shopInventoryTitle')}</h3>
            {inventoryLoading ? (
              <p className="no-items">{t('enhanceLoadingInventory')}</p>
            ) : (
              <div
                className="npc-shop-inventory-touch-host"
                onTouchMoveCapture={handleGridTouchMove}
                onTouchEndCapture={handleGridTouchEnd}
                onTouchCancelCapture={handleGridTouchCancel}
              >
                <InventoryBagGrid
                  gridSlots={inventoryGridSlots}
                  currentInventoryPage={currentInventoryPage}
                  onPageChange={setCurrentInventoryPage}
                  unlockedSlotsCount={unlockedSlotsCount}
                  dragOverIndex={combinedDragOverIndex}
                  isDropBlocked={isInventoryDropBlocked}
                  draggable={(slotItem) => !!slotItem && !buying && draggedShopIndex === null}
                  onDragStart={handleGridDragStart}
                  onDragOver={handleInventoryDragOver}
                  onDragLeave={handleInventoryDragLeave}
                  onDrop={handleInventoryDrop}
                  onDragEnd={handleGridDragEnd}
                  onTouchStart={handleGridTouchStart}
                  onClick={handleInventoryClick}
                  onPageTabDragOver={handleInventoryTabDragOver}
                  slotKeyPrefix="npc-shop-inventory"
                  wrapperClassName="npc-shop-inventory-grid-wrapper market-grid-wrapper"
                  gridClassName="market-grid inventory-grid-10x10"
                  areaRef={inventoryGridAreaRef}
                  slotExtraClassName="market-slot"
                  emptySlotTitle={ti('slotEmpty')}
                  lockedSlotTitle={ti('slotLocked')}
                  pageTabsClassName="inventory-page-tabs npc-shop-page"
                  isTouchDragging={combinedTouchDragging}
                />
              </div>
            )}
          </div>
        </div>

        {combinedTouchGhost ? (
          <div
            className="touch-drag-ghost"
            style={{ left: `${combinedTouchGhost.x}px`, top: `${combinedTouchGhost.y}px` }}
          >
            {combinedTouchGhost.imageUrl ? (
              <img src={combinedTouchGhost.imageUrl} alt="" draggable={false} />
            ) : null}
          </div>
        ) : null}
      </div>
    </>
  );
};

export default NpcShopWorkbench;
